// Atlas Dashboard — Login Page
// The page the Founder sees when they open the dashboard without being logged in.

import { useState, useEffect } from "react";
import { Activity, Mail, AlertCircle } from "lucide-react";
import { getAuthToken } from "../lib/api-client";

const API_BASE_URL = "https://atlas-api.crawwwlr-mail.workers.dev";

export function Login() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function handleConnect() {
    setLoading(true);
    setError(null);
    window.location.href = `${API_BASE_URL}/auth/connect`;
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get("error");
    if (errorParam) {
      const messages: Record<string, string> = {
        gmail_denied: "You didn't allow Atlas to access your Gmail. Atlas needs permission to track your emails.",
        no_code: "Something went wrong with the Google connection. Please try again.",
        token_exchange_failed: "Could not complete the Gmail connection. Please try again.",
      };
      setError(messages[errorParam] || params.get("message") || "An error occurred during Gmail connection.");
    }
  }, []);

  useEffect(() => {
    if (getAuthToken()) {
      window.location.href = "/";
    }
  }, []);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--color-background)",
        padding: "24px",
      }}
    >
      <div
        style={{
          backgroundColor: "var(--color-card)",
          borderRadius: "12px",
          border: "1px solid var(--color-border)",
          padding: "48px",
          maxWidth: "440px",
          width: "100%",
          boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "32px" }}>
          <Activity size={32} color="#2563EB" />
          <div>
            <h1 style={{ fontSize: "24px", fontWeight: 700, margin: 0, color: "var(--color-text-primary)" }}>
              Crawwwlr
            </h1>
            <div style={{ fontSize: "14px", color: "var(--color-text-tertiary)" }}>Email Intelligence</div>
          </div>
        </div>

        {error && (
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "8px",
              padding: "12px 16px",
              backgroundColor: "#FEF2F2",
              border: "1px solid #FECACA",
              borderRadius: "8px",
              marginBottom: "24px",
              fontSize: "14px",
              color: "#DC2626",
            }}
          >
            <AlertCircle size={16} style={{ flexShrink: 0, marginTop: "2px" }} />
            <span>{error}</span>
          </div>
        )}

        <p style={{ fontSize: "16px", color: "var(--color-text-secondary)", marginBottom: "32px", lineHeight: 1.6 }}>
          Crawwwlr tracks when your emails are opened, detects replies, and builds contact intelligence —
          all from your own Gmail, at no cost.
        </p>

        <button
          onClick={handleConnect}
          disabled={loading}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            width: "100%",
            padding: "14px",
            backgroundColor: "#2563EB",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "15px",
            fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.6 : 1,
            marginBottom: "16px",
          }}
        >
          <Mail size={18} />
          {loading ? "Connecting..." : "Connect your Gmail"}
        </button>

        <p style={{ fontSize: "12px", color: "var(--color-text-tertiary)", marginTop: "24px", lineHeight: 1.5, textAlign: "center" }}>
          Crawwwlr requests only the minimum Gmail permissions: read your mailbox (to detect replies)
          and send email (for campaigns). Crawwwlr cannot delete your emails, access your password, or
          share your data with anyone.
        </p>
      </div>
    </div>
  );
}
